import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { SeverityTypes } from '@src/types/Alerts';
import { Controller, useFormContext } from 'react-hook-form';
import AlertSeverityField from './AlertSeverityField';
import useAlias from '@alias';
import FormattedAlias from '@components/_shared/Translations/FormattedAlias';

const StyledTextField = styled(TextField)({
  margin: '10px 0',
});

type AlertAddEditFormProps = {
  topicFieldDisabled: boolean;
};

export default function AlertAddEditFormFields(props: AlertAddEditFormProps) {
  const alias = useAlias();
  const { control } = useFormContext();
  const { topicFieldDisabled } = props;

  return (
    <>
      <FormGroup>
        <Controller
          name='alertName'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <StyledTextField
              {...field}
              helperText={error ? error.message : null}
              error={!!error}
              fullWidth
              required
              type='text'
              label={alias('Alert Name')}
            />
          )}
        />
        <Controller
          name='topic'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <StyledTextField
              disabled={topicFieldDisabled}
              helperText={error ? error.message : null}
              error={!!error}
              {...field}
              fullWidth
              required
              type='text'
              label={alias('Alert Topic')}
            />
          )}
        />
        <Controller
          name='severity'
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel id='severity-select-label'>
                <FormattedAlias id='Severity' />
              </InputLabel>
              <Select
                error={!!error}
                value={value}
                onChange={onChange}
                labelId='severity-label'
                id='severity'
                required
                label={alias('Severity')}
              >
                {Object.entries(SeverityTypes).map(([_, val]) => {
                  if (typeof val === 'number') {
                    return (
                      <MenuItem key={val} value={val}>
                        <AlertSeverityField severity={val} />
                      </MenuItem>
                    );
                  }
                  return null;
                })}
              </Select>
            </FormControl>
          )}
        />

        <FormGroup sx={{ display: 'flex', flexDirection: 'row' }}>
          <Controller
            name='isEquipmentFault'
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormControlLabel
                sx={{
                  mt: 2,
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}
                control={<Checkbox checked={value} onChange={onChange} />}
                label={alias('Is Equipment Fault')}
              />
            )}
          />
          <Controller
            name='enableDynamicMessage'
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormControlLabel
                sx={{
                  mt: 2,
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}
                control={<Checkbox checked={value} onChange={onChange} />}
                label={alias('Enable Dynamic Message')}
              />
            )}
          />
        </FormGroup>
      </FormGroup>

      <Typography
        sx={{ mt: 3, mb: 2, lineHeight: 1.2 }}
        variant='subtitle1'
        align='center'
      >
        <FormattedAlias id='Threshold & Messaging' />
        <br />
        <sub>
          <FormattedAlias id='(at least one message is required)' />
        </sub>
      </Typography>

      <FormGroup sx={{ display: 'flex', flexDirection: 'row' }}>
        <Controller
          name='messageAboveMax'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <StyledTextField
              helperText={error ? error.message : null}
              error={!!error}
              {...field}
              sx={{ flexGrow: 1, mr: 2 }}
              type='text'
              label={alias('Message Above Max')}
            />
          )}
        />
        <Controller
          name='thresholdMax'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <StyledTextField
              helperText={error ? error.message : null}
              error={!!error}
              {...field}
              type='number'
              sx={{ width: 120 }}
              label={alias('Threshold Max')}
            />
          )}
        />
      </FormGroup>

      <FormGroup sx={{ display: 'flex', flexDirection: 'row' }}>
        <Controller
          name='messageBelowMin'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <StyledTextField
              helperText={error ? error.message : null}
              error={!!error}
              {...field}
              sx={{ flexGrow: 1, mr: 2 }}
              type='text'
              label={alias('Message Below Min')}
            />
          )}
        />
        <Controller
          name='thresholdMin'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <StyledTextField
              helperText={error ? error.message : null}
              error={!!error}
              {...field}
              type='number'
              sx={{ width: 120 }}
              label={alias('Threshold Min')}
            />
          )}
        />
      </FormGroup>

      <Typography sx={{ mt: 3, mb: 2 }} variant='subtitle1' align='center'>
        <FormattedAlias id='Timeouts' />
      </Typography>
      <FormGroup
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '20px',
        }}
      >
        <Controller
          name='timeoutActivateMs'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <StyledTextField
              helperText={error ? error.message : null}
              error={!!error}
              {...field}
              type='number'
              label={alias('Timeout Activate (ms)')}
            />
          )}
        />
        <Controller
          name='timeoutRemoveMs'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <StyledTextField
              helperText={error ? error.message : null}
              error={!!error}
              {...field}
              type='number'
              label={alias('Timeout Remove (ms)')}
            />
          )}
        />
      </FormGroup>

      <Typography sx={{ mt: 3, mb: 2 }} variant='subtitle1' align='center'>
        <FormattedAlias id='Custom Mappings' />
      </Typography>
      <FormGroup>
        <Controller
          name='valueMappings'
          control={control}
          render={({ field: { value = {}, onChange } }) => {
            // console.log('valueMappings', value);

            const mappingEntries = Object.entries(value);

            return (
              <>
                {mappingEntries.map(([key, mappingValue], index) => (
                  <FormGroup
                    key={`${key}-${index}`}
                    sx={{ display: 'flex', flexDirection: 'row', mb: 2 }}
                  >
                    <StyledTextField
                      value={key}
                      onChange={(e) => {
                        const newMappings = { ...value };
                        delete newMappings[key];
                        newMappings[e.target.value] = mappingValue;
                        onChange(newMappings);
                      }}
                      sx={{ flexGrow: 1, mr: 2 }}
                      type='text'
                      label={alias('Key')}
                    />
                    <StyledTextField
                      value={mappingValue as string}
                      onChange={(e) => {
                        const newMappings = { ...value };
                        newMappings[key] = e.target.value;
                        onChange(newMappings);
                      }}
                      sx={{ flexGrow: 1, mr: 2 }}
                      type='text'
                      label={alias('Value')}
                    />
                    <Button
                      onClick={() => {
                        const newMappings = { ...value };
                        delete newMappings[key];
                        onChange(newMappings);
                      }}
                      style={{ padding: '8px 12px' }}
                    >
                      <FormattedAlias id='Remove' />
                    </Button>
                  </FormGroup>
                ))}
                <Button
                  type='button'
                  onClick={() => {
                    const newKey = `key${Object.keys(value).length + 1}`;
                    onChange({ ...value, [newKey]: '' });
                  }}
                  style={{ marginTop: '10px', padding: '8px 16px' }}
                >
                  <FormattedAlias id='Add Mapping' />
                </Button>
              </>
            );
          }}
        />
      </FormGroup>
    </>
  );
}
